// Port of companion/src/script/compile.mjs at tag js-core-freeze-2026-09-05 (HEAD cb2d250), carried
// into Round 1 (docs/briefs/ROUND1-SPEC-2026-09-05.md, lane B: sections A, C, D, E, K, H rows B).
// What the frozen reference still decides is kept byte for byte: the synthetic id counters, the
// request shapes, the frozen structural kinds and reasons, the scene alignment, the hub compare,
// the graph report's counts. What Round 1 changes on purpose is named where it happens:
//   - identity (A): per-scene step alignment by signature, then similarity, then equal-count
//     position (script/align.ts) replaces the frozen two-pass LCS; a moved line is `move_step`;
//     answers are bound by their own ids (alignAnswers), never by index; nothing changed is re-created;
//   - the sidecar (3.1): version 3 is read as written, version 2 is upgraded in memory (kind from
//     the step type, sig recomputed), any other version is refused;
//   - every step type (C): endings, texting/thinking/reaction, video, achieve, roll, legacy and the
//     check envelope compile fresh and compare mapped; a type-5 step makes its node an ending (C.5);
//   - the choice prompt's speaker (D): fresh choices carry it; mapped compiles never assign a null
//     cast for a name or mood that did not resolve;
//   - check and remember (E): an unknown operator produces nothing; the verb travels; a typed value
//     change is a change; a no-edit pass leaves the step's own objects in place;
//   - the write shape (F, lane C's modules): a scene is cut before and after every choice, check and
//     hub (splitScene); node titles are slugs and the heading is the description (slugTitle,
//     uniqueSlug); answerTypeId follows the price (answerTypeForPrice).
// Spellings that differ from the frozen file for the editor-client lint profile and nothing else:
// `for...of` is forEach or an index loop, `continue` is an early return, `a++` is `a += 1`.
// The spell check meets two words of this file’s own vocabulary: cspell:ignore KINDED unrouted
import type {
  Answer,
  AppendableStep,
  AppendableStepTypeId,
  Block,
  Branch,
  CharacterBlock,
  CheckEnvelope,
  ChoiceAnswer,
  ChoiceBlock,
  CompileOptions,
  CompileResult,
  EditRequest,
  EndDirective,
  EngineId,
  Fixture,
  FreshCompile,
  HubOption,
  HubSettings,
  IfDirective,
  LegacyBlock,
  HubBlock,
  MappedCompile,
  Marker,
  RawMemoryValue,
  RawRangeRoll,
  RawRoll,
  ReportEnding,
  RollBlock,
  Scene,
  ScriptAst,
  SetDirective,
  SidecarBlock3,
  SidecarInput,
  SidecarKind3,
  Step,
  StructuralItem,
  StructuralKind,
  StructuralStatus,
  StructureReport,
  SwitchArm,
  VideoBlock,
} from '../../../dorian-shared/types/episodeScript';
import { deepClone } from '../deepClone';
import { answerTypeForPrice } from '../economy';
import { findBranch, findStep, sameId } from '../model';
import { S } from '../projection';
import { slugTitle, uniqueSlug } from '../slug';
import { splitScene } from '../validate';
import type { AlignableBlock, AlignableEntry, AnswerIdentity } from './align';
import { alignAnswers, alignSteps, normaliseText } from './align';
import {
  blockSignature,
  sceneSignature,
  stepSignature,
  uniqueSceneNames,
} from './decompile';

/** makeCounters' five counters (compile.mjs:22-35): each call yields the next negative id of its kind. */
interface Counters {
  branch: () => number;
  step: () => number;
  answer: () => number;
  option: () => number;
  variable: () => number;
}

/** A directive or block that names a variable — VARIABLE_DIRECTIVES (compile.mjs:37) plus the Round 1 `> roll:`. */
type VariableBlock = IfDirective | SetDirective | RollBlock;

/** mintVariables' table (compile.mjs:45-57): lower-cased name → the minted id and the name as first written. */
type MintedVariables = Map<string, { id: number; name: string }>;

/** Anything routed by scene name: an answer, a check arm, a hub card, a `> go to:`. */
interface Targeted {
  target: string | null;
}

/** Lower-cased scene name → node id, the table every route resolves through (compile.mjs:77-80). */
type Targets = Map<string, EngineId | undefined>;

/** graphReport's third argument (compile.mjs:145-147). */
interface ReportOptions {
  variableNames?: Map<string, string>;
  endings?: Map<string, string>;
}

/** One part of a scene after F.1's cut: the blocks of one node. */
interface ScenePart {
  scene: Scene;
  blocks: Block[];
  part: number;
}

/** A scene part with its minted node id (compile.mjs:223). */
interface IdentifiedPart extends ScenePart {
  id: number;
}

/** F.2: what a node is called — the slug title and the writer's heading as its description. */
interface NodeNaming {
  title: string;
  description: string;
}

/** The details addStructural gives structuralItem (compile.mjs:452-457), in this key order. */
interface StructuralDetails {
  scene: string | null;
  branchId: EngineId | null;
  stepId: EngineId | null;
  line: number | null;
  reason: string;
}

/** What the alignment keys on: a content signature and a block kind (SIGNED and KINDED, compile.mjs:345-346). */
interface Keyed {
  sig: string | null;
  kind: string;
}

type KeyOf = (item: Keyed) => string | null;

/** One position of the merged order (alignSequence, compile.mjs:316-343): a null side is a delete or an insert. */
interface Aligned<B, A> {
  before: B | null;
  after: A | null;
}

/** A map entry with its signature, on the before side of the scene alignment. */
interface IdentityItem extends Keyed {
  identity: SidecarBlock3;
}

/** A parsed scene with its signature, on the after side. */
interface SceneItem extends Keyed {
  scene: Scene;
}

/** What addStructural reads of an identity: a map entry, or the `{ branchId, stepId, line }` of a step just made. */
interface IdentityHint {
  branchId: EngineId | null;
  stepId: EngineId | null;
  line: number | null;
}

/** The speaker fields a character line or a choice's NAME line carries (D.3). */
interface CastFields {
  characterId?: EngineId | null;
  expression?: string | null;
  characterExpressionId?: EngineId | null;
}

// ─── Small helpers (frozen unless marked) ──────────────────────────────────────────────────────

function sameValue(left: unknown, right: unknown): boolean {
  return S(left) === S(right);
}

function blankEqual(left: unknown, right: unknown): boolean {
  return String(left ?? '') === String(right ?? '');
}

/**
 * E.4 / BAR-R1-05: JSON equality with the types kept. The scalar projection `S` spells an array and
 * a string that looks like one the same way, so the `> check:` envelope — which is JSON on the page
 * and JSON in the engine — is compared as JSON: same shape, same values, same types. Key order does
 * not count; JSON has no undefined, so a key that is absent on one side and undefined on the other
 * is the same absence. This is deliberately outside the scalar-CHECK type tolerance of E.6.
 */
function jsonEqual(left: unknown, right: unknown): boolean {
  if (left === null || right === null || typeof left !== 'object' || typeof right !== 'object') {
    return typeof left === typeof right && Object.is(left, right);
  }
  if (Array.isArray(left) !== Array.isArray(right)) return false;
  const leftRecord = left as Record<string, unknown>;
  const rightRecord = right as Record<string, unknown>;
  const keys = new Set([...Object.keys(leftRecord), ...Object.keys(rightRecord)]);
  return [...keys].every((key) => jsonEqual(leftRecord[key], rightRecord[key]));
}

function plain(value: unknown): string {
  return value === null || value === undefined ? '' : String(value);
}

/** decompile.mjs:129 — the slot a variable id fills in a signature or a pairing text. */
function variableSlot(id: unknown): string {
  return id == null ? '?' : String(id);
}

function commonStep(id: number, branchId: EngineId, stepTypeId: number, text: string | null = null): Step {
  return {
    id,
    branchId,
    stepTypeId,
    text,
    characterId: null,
    characterExpressionId: null,
    characterPlacementSide: 'default',
    subType: null,
    characterRelationship: null,
    currencyToggleSettings: null,
    stepAudios: [],
    data: [],
    answers: [],
    wordCount: 0,
  };
}

// compile.mjs:22-35. Each counter starts at -1 and hands out its current value before stepping down.
function counter(): () => number {
  let next = -1;
  return () => {
    const id = next;
    next -= 1;
    return id;
  };
}

function makeCounters(): Counters {
  return {
    branch: counter(),
    step: counter(),
    answer: counter(),
    option: counter(),
    variable: counter(),
  };
}

// ─── Step kinds (3.1) ──────────────────────────────────────────────────────────────────────────

const KIND_BY_STEP_TYPE: Readonly<Record<number, SidecarKind3>> = Object.freeze({
  1: 'character',
  8: 'character',
  9: 'character',
  10: 'character',
  2: 'narration',
  3: 'choice',
  5: 'ending',
  6: 'legacy',
  7: 'legacy',
  11: 'check',
  12: 'remember',
  13: 'achieve',
  14: 'video',
  15: 'roll',
  16: 'hub',
});

/** 3.1: the sidecar kind of an engine step type; a type the table does not know reads as narration (the frozen fallback). */
export function kindOfStepType(stepTypeId: unknown): SidecarKind3 {
  return KIND_BY_STEP_TYPE[Number(stepTypeId)] ?? 'narration';
}

const STEP_TYPE_BY_MODE = Object.freeze({ texting: 8, thinking: 9, reaction: 10 } as const);

const ENDING_STEP_TYPE = 5;
const REACTION_STEP_TYPE = 10;
const CHOICE_STEP_TYPE = 3;
const CHECK_STEP_TYPE = 11;
const HUB_STEP_TYPE = 16;
const CHARACTER_STEP_TYPE = 1;

/** C: the step type a character line compiles to — 1, or 8/9/10 by its tag. */
function characterStepType(block: CharacterBlock): number {
  return block.mode ? STEP_TYPE_BY_MODE[block.mode] ?? CHARACTER_STEP_TYPE : CHARACTER_STEP_TYPE;
}

function isRememberBlock(block: Block): block is SetDirective {
  return block.type === 'directive'
    && (block.directive === 'remember' || block.directive === 'forget' || block.directive === 'view');
}

function isVariableBlock(block: Block): block is VariableBlock {
  if (block.type === 'roll') return true;
  return block.type === 'directive' && (block.directive === 'if' || isRememberBlock(block));
}

/** A.3: the sidecar kind a parsed block claims; null for a block that is not a step (a note, a `> go to:`). */
function blockKind(block: Block): SidecarKind3 | null {
  switch (block.type) {
    case 'character': return 'character';
    case 'narration': return 'narration';
    case 'choice': return 'choice';
    case 'hub': return 'hub';
    case 'video': return 'video';
    case 'achieve': return 'achieve';
    case 'roll': return 'roll';
    case 'legacy': return 'legacy';
    case 'directive':
      if (block.directive === 'if' || block.directive === 'check') return 'check';
      if (block.directive === 'end') return 'ending';
      if (isRememberBlock(block)) return 'remember';
      return null;
    default:
      return null;
  }
}

function isStepBlock(block: Block): boolean {
  return blockKind(block) !== null;
}

// U+0001, spelled by code point (decompile.mjs:117): the character itself is invisible.
const SEPARATOR = String.fromCharCode(1);

function signature(kind: string, ...parts: unknown[]): string {
  return [kind, ...parts.map(normaliseText)].join(SEPARATOR);
}

// A.2 for the Round 1 blocks the frozen blockSignature does not know. Lane A's decompile extends
// blockSignature with the same strings; this fallback only answers where that returns null.
// stub until lane A
function round1BlockSignature(block: Block): string | null {
  const frozen = blockSignature(block);
  if (frozen != null) return frozen;
  if (block.type === 'directive' && block.directive === 'end') return signature('ending', block.text ?? '');
  if (block.type === 'directive' && block.directive === 'check') return signature('directive', 'if', JSON.stringify(block.payload.check));
  if (block.type === 'directive' && block.directive === 'view') return signature('directive', 'set', variableSlot(block.variableId), block.value);
  if (block.type === 'legacy') return signature('legacy', block.stepTypeId, block.text);
  if (block.type === 'video') return signature('video', '');
  if (block.type === 'achieve') return signature('achieve', '');
  if (block.type === 'roll') return signature('roll', JSON.stringify(block.roll));
  return null;
}

/** C, type 14: the label the script shows for a video step — `video.title`, or `video <id>` when the title is empty. */
export function videoLabel(step: Step): string {
  const title = String(step.video?.title ?? '').trim();
  if (title) return title;
  return `video ${plain(step.video?.id)}`.trim();
}

function hubTitles(step: Step): string[] {
  return [...(step.hubOptions ?? [])]
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((option) => String(option.title ?? ''));
}

/** A.3: the pairing text of an engine step, per kind. */
export function stepPairingText(step: Step): string {
  switch (kindOfStepType(step.stepTypeId)) {
    case 'choice':
      return [String(step.text ?? ''), ...(step.answers ?? []).map((answer) => String(answer.text ?? ''))].join(' ');
    case 'check':
      return `${variableSlot(step.check?.variableId)} ${plain(step.check?.operator)}`;
    case 'remember':
      return variableSlot(step.action?.variableId);
    case 'hub':
      return [plain(step.hubSettings?.header?.text), plain(step.hubSettings?.subheader?.text), ...hubTitles(step)].join(' ');
    case 'video':
      return videoLabel(step);
    case 'achieve':
      return String(step.achievementDescription ?? '');
    case 'roll':
      return variableSlot(step.roll?.variableId);
    default:
      return String(step.text ?? '');
  }
}

/** A.3: the pairing text of a parsed step block, per kind — the same words the step side yields. */
export function blockPairingText(block: Block): string {
  switch (block.type) {
    case 'character':
    case 'narration':
    case 'legacy':
      return block.text;
    case 'choice':
      return [block.text, ...block.answers.map((answer) => answer.text)].join(' ');
    case 'hub':
      return [block.header, block.subheader, ...block.options.map((option) => option.text)].join(' ');
    case 'video':
      return block.label;
    case 'achieve':
      return block.description;
    case 'roll':
      return variableSlot(block.variableId);
    case 'directive':
      if (block.directive === 'if') return `${variableSlot(block.variableId)} ${plain(block.operator)}`;
      if (block.directive === 'check') {
        const check = (block.payload.check ?? {}) as { variableId?: unknown; operator?: unknown };
        return `${variableSlot(check.variableId)} ${plain(check.operator)}`;
      }
      if (block.directive === 'end') return block.text ?? '';
      if (isRememberBlock(block)) return variableSlot(block.variableId);
      return '';
    default:
      return '';
  }
}

// U+0002: the join of a raw identity key's own parts, so a raw text can never be read as a signature.
const RAW_SEPARATOR = String.fromCharCode(2);

/**
 * BAR-R1-01: the exact text a step's signature normalised — '' for a kind whose signature carries no
 * free text at all (a check, a remember, a roll, a video, an achieve, a reaction line). It mirrors
 * stepSignature case for case; anything else would refine the wrong field.
 */
function stepRawText(step: Step): string {
  const kind = kindOfStepType(step.stepTypeId);
  if (kind === 'character') {
    // A speaker the script cannot name signs as its paragraph; a reaction is one line and has none.
    if (step.characterId == null) return String(step.text ?? '');
    return Number(step.stepTypeId) === REACTION_STEP_TYPE ? '' : String(step.text ?? '');
  }
  if (kind === 'choice') {
    return [String(step.text ?? ''), ...(step.answers ?? []).map((answer) => String(answer.text ?? ''))].join(RAW_SEPARATOR);
  }
  if (kind === 'hub') {
    return [plain(step.hubSettings?.header?.text), plain(step.hubSettings?.subheader?.text), ...hubTitles(step)].join(RAW_SEPARATOR);
  }
  if (kind === 'narration' || kind === 'ending' || kind === 'legacy') return String(step.text ?? '');
  return '';
}

/** BAR-R1-01: the same text, read off the parsed block — the words the writer left on the page. */
function blockRawText(block: Block): string {
  if (block.type === 'character') return block.mode === 'reaction' ? '' : block.text;
  if (block.type === 'narration' || block.type === 'legacy') return block.text;
  if (block.type === 'choice') return [block.text, ...block.answers.map((answer) => answer.text)].join(RAW_SEPARATOR);
  if (block.type === 'hub') {
    return [block.header, block.subheader, ...block.options.map((option) => option.text)].join(RAW_SEPARATOR);
  }
  if (block.type === 'directive' && block.directive === 'end') return block.text ?? '';
  return '';
}

/**
 * BAR-R1-01: a raw identity key — the signature refined by the exact text it normalised. Two items
 * with the same key are the same words letter for letter, so the key is strictly narrower than the
 * signature: a raw match is always a signature match, never the other way about.
 */
function rawKey(sig: string | null, text: string): string | null {
  return sig == null ? null : `${sig}${RAW_SEPARATOR}${text}`;
}

// ─── Variables (frozen, widened to `> roll:`) ──────────────────────────────────────────────────

function variableKey(block: VariableBlock): string {
  return String(block.variable ?? '').trim().toLocaleLowerCase();
}

// A fresh compile has no memory bank to match names against, so every name the
// parser could not resolve becomes a NEW variable with a synthetic negative id.
function mintVariables(ast: ScriptAst, counters: Counters): MintedVariables {
  const minted: MintedVariables = new Map();
  ast.scenes.forEach((scene) => {
    scene.blocks.forEach((block) => {
      if (!isVariableBlock(block)) return;
      if (block.variableId != null) return;
      const key = variableKey(block);
      if (!key || minted.has(key)) return;
      minted.set(key, { id: counters.variable(), name: block.variable });
    });
  });
  return minted;
}

function variableIdOf(block: VariableBlock, minted: MintedVariables | null): EngineId | null {
  if (block.variableId != null) return block.variableId;
  return minted?.get(variableKey(block))?.id ?? null;
}

function variableNames(ast: ScriptAst, minted: MintedVariables): Map<string, string> {
  const names = new Map<string, string>();
  ast.scenes.forEach((scene) => {
    scene.blocks.forEach((block) => {
      if (!isVariableBlock(block)) return;
      const id = variableIdOf(block, minted);
      if (id == null) return;
      if (!names.has(String(id))) names.set(String(id), block.variable ?? `V${id}`);
    });
  });
  return names;
}

function targetId(item: Targeted, targets: Targets): EngineId | null {
  if (!item?.target) return null;
  return targets.get(item.target.toLocaleLowerCase()) ?? null;
}

// ─── Blocks to steps (fresh, and the fresh half of mapped) ─────────────────────────────────────

/** C: the text of a `> end:` line — `text` from lane A's parser; the frozen parser's `name` when it is not the scene's. */
function endText(block: EndDirective, sceneName: string): string {
  if (block.text !== undefined) return block.text;
  return block.name !== sceneName ? block.name : '';
}

/** C, type 15: the parsed roll as the engine stores it (`variableId` second, `modifier` only when given). */
function rollFromBlock(block: RollBlock, variableId: EngineId | null): RawRoll {
  const id = variableId as EngineId;
  if (block.roll.type === 'dice') return { type: 'dice', variableId: id, diceNotation: block.roll.diceNotation };
  const roll: RawRangeRoll = {
    type: block.roll.type,
    variableId: id,
    min: block.roll.min as RawMemoryValue,
    max: block.roll.max as RawMemoryValue,
  };
  if (block.roll.modifier !== undefined) roll.modifier = block.roll.modifier;
  return roll;
}

/** F.5 and the frozen answer shape (compile.mjs:93-107): a fresh answer with the compiler-opaque fields empty. */
function freshAnswer(answer: ChoiceAnswer, counters: Counters, targets: Targets): Answer {
  return {
    id: counters.answer(),
    text: answer.text,
    answerTypeId: answerTypeForPrice(answer.price ?? 0, null),
    gotoBranchId: targetId(answer, targets),
    answerPrice: answer.price ?? 0,
    eliminationResultType: 'none',
    eliminationResultText: '',
    requirement: null,
    traits: [],
    requires: [],
    data: [],
    tags: [],
    modifiers: [],
  };
}

const UNRESOLVED_LIBRARY_ITEM = 'unresolved library item';

function blockToStep(
  block: Block,
  branchId: EngineId,
  targets: Targets,
  counters: Counters,
  minted: MintedVariables | null,
  markers: Marker[],
  sceneName: string,
): Step | null {
  if (block.type === 'character') {
    const stepTypeId = characterStepType(block);
    const step = commonStep(counters.step(), branchId, stepTypeId, stepTypeId === REACTION_STEP_TYPE ? null : block.text);
    step.characterId = block.characterId;
    step.characterExpressionId = block.characterExpressionId;
    return step;
  }
  if (block.type === 'narration') return commonStep(counters.step(), branchId, 2, block.text);
  if (block.type === 'choice') {
    const step = commonStep(counters.step(), branchId, CHOICE_STEP_TYPE, block.text);
    // D: the prompt's speaker travels with the choice (frozen fresh choices carried none).
    step.characterId = block.characterId ?? null;
    step.characterExpressionId = block.characterExpressionId ?? null;
    step.currencyToggleSettings = 'heartsFirst';
    step.answers = block.answers.map((answer) => freshAnswer(answer, counters, targets));
    return step;
  }
  if (block.type === 'directive' && block.directive === 'if') {
    // E.2: an unknown operator was diagnosed by the parser and produces no step.
    if (block.operator === null) return null;
    const step = commonStep(counters.step(), branchId, CHECK_STEP_TYPE, null);
    step.check = { variableId: variableIdOf(block, minted), operator: block.operator, value: block.value };
    step.switch = block.arms.map((arm): SwitchArm => ({ value: arm.value, gotoBranchId: targetId(arm, targets) }));
    return step;
  }
  if (block.type === 'directive' && block.directive === 'check') {
    // E.4: the envelope is written as given; the ids inside it are the engine's own.
    const step = commonStep(counters.step(), branchId, CHECK_STEP_TYPE, null);
    step.check = block.payload.check as Step['check'];
    step.switch = block.payload.switch as SwitchArm[];
    return step;
  }
  if (isRememberBlock(block)) {
    const step = commonStep(counters.step(), branchId, 12, null);
    // E.5: the verb is written into action.type; `+=` is increase, never set.
    step.action = { type: block.verb ?? 'set', value: block.value, variableId: variableIdOf(block, minted) };
    return step;
  }
  if (block.type === 'directive' && block.directive === 'end') {
    return commonStep(counters.step(), branchId, ENDING_STEP_TYPE, endText(block, sceneName));
  }
  if (block.type === 'video') {
    const step = commonStep(counters.step(), branchId, 14, block.text || null);
    markers.push({ line: block.line, value: block.label, reason: UNRESOLVED_LIBRARY_ITEM });
    return step;
  }
  if (block.type === 'achieve') {
    const step = commonStep(counters.step(), branchId, 13, null);
    step.achievementDescription = block.description;
    markers.push({ line: block.line, value: block.description, reason: UNRESOLVED_LIBRARY_ITEM });
    return step;
  }
  if (block.type === 'roll') {
    const step = commonStep(counters.step(), branchId, 15, null);
    step.roll = rollFromBlock(block, variableIdOf(block, minted));
    return step;
  }
  if (block.type === 'legacy') {
    const step = commonStep(counters.step(), branchId, block.stepTypeId, block.text);
    step.subType = block.subType;
    return step;
  }
  if (block.type === 'hub') {
    const step = commonStep(counters.step(), branchId, HUB_STEP_TYPE, null);
    step.hubSettings = {
      id: counters.option(),
      stepId: step.id,
      header: { text: block.header },
      subheader: { text: block.subheader },
      progressBars: [],
    };
    step.hubOptions = block.options.map((option, index): HubOption => ({
      id: counters.option(),
      stepId: step.id,
      sortOrder: index,
      viewType: 'card',
      title: option.text,
      answerPrice: option.price ?? 0,
      gotoBranchId: targetId(option, targets),
      iconId: null,
      borderColor: null,
      state: { state: 'active' },
      score: null,
      replay: null,
      mods: [],
      tags: null,
    }));
    return step;
  }
  return null;
}

function makeBranch(
  scene: Scene,
  id: EngineId,
  targets: Targets,
  counters: Counters,
  minted: MintedVariables | null,
  naming: NodeNaming,
  markers: Marker[],
): Branch {
  const steps: Step[] = [];
  const branch: Branch = {
    id,
    title: naming.title,
    description: naming.description,
    gotoBranchId: null,
    stepTypeMode: 'regular',
    locationId: scene.locationId,
    pos: null,
    steps,
  };
  scene.blocks.forEach((block) => {
    if (block.type === 'directive' && block.directive === 'goto') {
      branch.gotoBranchId = targetId(block, targets);
      return;
    }
    const step = blockToStep(block, id, targets, counters, minted, markers, scene.name);
    if (step) steps.push(step);
  });
  return branch;
}

// ─── The graph report (frozen counts; C.5 endings) ─────────────────────────────────────────────

function graphReport(nodes: Branch[], markers?: Marker[], options: ReportOptions = {}): StructureReport {
  const names = options.variableNames ?? new Map<string, string>();
  const endings = options.endings ?? new Map<string, string>();
  const ids = new Set(nodes.map((node) => String(node.id)));
  const outgoing = new Map(nodes.map((node): [string, Set<string>] => [String(node.id), new Set()]));
  const variables = new Set<string>();
  let choiceBlocks = 0;
  let choices = 0;
  let paidChoices = 0;
  let checks = 0;
  let hubs = 0;
  const addEdge = (from: EngineId, to: EngineId | null | undefined): void => {
    if (to != null && ids.has(String(to))) outgoing.get(String(from))?.add(String(to));
  };
  nodes.forEach((node) => {
    addEdge(node.id, node.gotoBranchId);
    (node.steps ?? []).forEach((step) => {
      if (step.stepTypeId === 3) {
        choiceBlocks += 1;
        choices += (step.answers ?? []).length;
        paidChoices += (step.answers ?? []).filter((answer) => Number(answer.answerPrice) > 0).length;
      }
      if (step.stepTypeId === 11) {
        checks += 1;
        if (step.check?.variableId != null) variables.add(String(step.check.variableId));
      }
      if (step.stepTypeId === 12 && step.action?.variableId != null) variables.add(String(step.action.variableId));
      if (step.stepTypeId === 16) hubs += 1;
      (step.answers ?? []).forEach((answer) => addEdge(node.id, answer.gotoBranchId));
      (step.switch ?? []).forEach((arm) => addEdge(node.id, arm.gotoBranchId));
      (step.hubOptions ?? []).forEach((option) => addEdge(node.id, option.gotoBranchId));
    });
  });
  const reached = new Set<string>();
  const queue = nodes.length ? [String(nodes[0].id)] : [];
  while (queue.length) {
    const id = queue.shift() as string;
    if (!reached.has(id)) {
      reached.add(id);
      queue.push(...(outgoing.get(id) ?? []));
    }
  }
  // An ending is a terminal scene on purpose (`> end`, frozen) or a node that holds a type-5 step
  // (C.5: how mapped ep02 and the synthetic fixture are silent on R3), so it is never a dead end.
  // Its name is the `> end:` text when there is one, else the scene's own name.
  const endingName = (node: Branch): string | null => {
    const named = endings.get(String(node.id));
    if (named !== undefined) return named;
    const ending = (node.steps ?? []).find((step) => Number(step.stepTypeId) === ENDING_STEP_TYPE);
    if (!ending) return null;
    const text = String(ending.text ?? '').trim();
    return text || String(node.description || node.title || `Node ${node.id}`);
  };
  const endingList: ReportEnding[] = [];
  const endingIds = new Set<string>();
  nodes.forEach((node) => {
    const name = endingName(node);
    if (name === null) return;
    endingIds.add(String(node.id));
    endingList.push({ id: node.id, name });
  });
  return {
    nodeCount: nodes.length,
    choiceBlocks,
    choiceCount: choices,
    paidChoices,
    checks,
    variables: [...variables].map((id) => ({
      id: /^-?\d+$/.test(id) ? Number(id) : id,
      name: names.get(id) ?? `V${id}`,
    })),
    variableCount: variables.size,
    hubs,
    endings: endingList,
    endingCount: endingList.length,
    deadEnds: nodes
      .filter((node) => outgoing.get(String(node.id))?.size === 0 && !endingIds.has(String(node.id)))
      .map((node) => node.id),
    unreachable: nodes.filter((node) => !reached.has(String(node.id))).map((node) => node.id),
    markers: deepClone(markers ?? []),
  };
}

// ─── Fresh compile (F.1 parts, F.2 names, C.5 endings) ─────────────────────────────────────────

type PartKind = 'choice' | 'check' | 'hub' | 'plain';

/** F.1: what a part is, read off the node it made. */
function partKindOf(node: Branch): PartKind {
  const isolated = (node.steps ?? []).find((step) => [CHOICE_STEP_TYPE, CHECK_STEP_TYPE, HUB_STEP_TYPE].includes(Number(step.stepTypeId)));
  if (!isolated) return 'plain';
  if (Number(isolated.stepTypeId) === CHOICE_STEP_TYPE) return 'choice';
  return Number(isolated.stepTypeId) === CHECK_STEP_TYPE ? 'check' : 'hub';
}

/** F.1: an unrouted answer or arm of the node's isolated step continues to `to`. */
function routeUnrouted(node: Branch, to: EngineId): void {
  (node.steps ?? []).forEach((step) => {
    if (Number(step.stepTypeId) === CHECK_STEP_TYPE) {
      (step.switch ?? []).forEach((arm) => {
        if (arm.gotoBranchId == null) arm.gotoBranchId = to; // eslint-disable-line no-param-reassign
      });
    }
    if (Number(step.stepTypeId) === CHOICE_STEP_TYPE) {
      (step.answers ?? []).forEach((answer) => {
        if (answer.gotoBranchId == null) answer.gotoBranchId = to; // eslint-disable-line no-param-reassign
      });
    }
  });
}

function compileFresh(ast: ScriptAst): FreshCompile {
  const counters = makeCounters();
  const minted = mintVariables(ast, counters);
  const compilerMarkers: Marker[] = [];
  // F.1: a scene is cut before and after every choice, check and hub (lane C's splitScene).
  const pieces: ScenePart[] = [];
  ast.scenes.forEach((scene) => {
    splitScene(scene.blocks).forEach((blocks, part) => {
      pieces.push({ scene, blocks, part });
    });
  });
  const parts: IdentifiedPart[] = pieces.map((part) => ({ ...part, id: counters.branch() }));
  const targets: Targets = new Map();
  parts.forEach((part) => {
    if (part.part === 0) targets.set(part.scene.name.toLocaleLowerCase(), part.id);
  });
  // F.2: the title is a slug of the heading (continuations `_2`, `_3`, …), unique across the compile;
  // the heading itself is the description, so a fresh node reads back under the writer's own words.
  const taken = new Set<string>();
  const nodes = parts.map((part) => {
    const slug = slugTitle(part.scene.name);
    const title = uniqueSlug(part.part === 0 ? slug : `${slug}_${part.part + 1}`, taken);
    taken.add(title);
    return makeBranch(
      { ...part.scene, blocks: part.blocks },
      part.id,
      targets,
      counters,
      minted,
      { title, description: part.scene.name },
      compilerMarkers,
    );
  });
  // `> end` (or the margin note that predates it) marks the last part of a scene
  // as terminal: it does not fall through to the next scene in the file.
  const endings = new Map<string, string>();
  ast.scenes.forEach((scene) => {
    if (!scene.ending) return;
    const own = parts.filter((part) => part.scene === scene);
    const last = own[own.length - 1];
    if (last) endings.set(String(last.id), scene.ending.name ?? scene.name);
  });
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    const kind = partKindOf(node);
    // F.1: a `> go to:` written after a choice or a check is the continuation of its unrouted
    // answers or arms — a regular node holding one may not carry a node route (validateBranch :93).
    if ((kind === 'choice' || kind === 'check') && node.gotoBranchId != null) {
      routeUnrouted(node, node.gotoBranchId);
      node.gotoBranchId = null;
    }
    if (!endings.has(String(node.id))) {
      const nextId = nodes[index + 1]?.id ?? null;
      const continuesTheScene = parts[index + 1]?.scene === parts[index].scene;
      // Frozen (compile.mjs:244-246, kept verbatim by F.1's "the last part follows the frozen
      // rules"): a part that WROTE a route of its own — a `> go to:`, a check, a choice or a hub —
      // does not also fall through to the next scene. A `> go to:` whose target does not resolve
      // leaves gotoBranchId null, and without this predicate that null reads as "nothing written"
      // and the node silently falls through to whatever follows it in the file.
      const hasExplicitRoute = parts[index].blocks.some((block) => (
        (block.type === 'directive' && (block.directive === 'goto' || block.directive === 'if'))
        || block.type === 'choice' || block.type === 'hub'));
      if (nextId != null) {
        // F.1: every part but the last continues into the next part of its scene — a choice or
        // check through its unrouted answers or arms, anything else through the node's route. The
        // last part follows the frozen rules: an explicit `> go to:`, `> end`, or the fall-through
        // to the next scene's first part (a hub never falls through, as frozen).
        if (kind === 'choice' || kind === 'check') routeUnrouted(node, nextId);
        else if (kind === 'hub') {
          if (continuesTheScene && node.gotoBranchId == null) node.gotoBranchId = nextId;
        } else if (node.gotoBranchId == null && (continuesTheScene || !hasExplicitRoute)) {
          node.gotoBranchId = nextId;
        }
      }
    }
  }
  // In a fresh compile there is no memory bank to be unknown to: a name the
  // parser could not resolve is a new variable the owner has to create.
  const markers = (ast.markers ?? []).map((item): Marker => (
    item.reason === 'unknown variable' && minted.has(String(item.value ?? '').trim().toLocaleLowerCase())
      ? { ...item, reason: 'new variable' }
      : item));
  const allMarkers = compilerMarkers.length ? [...markers, ...compilerMarkers] : markers;
  const report = graphReport(nodes, allMarkers, { variableNames: variableNames(ast, minted), endings });
  return {
    mode: 'fresh', nodes, report, markers: allMarkers,
  };
}

// ─── Mapped compile ────────────────────────────────────────────────────────────────────────────

const NEEDS_CANARY: StructuralStatus = 'needs canary';
// The one class of change the compiler will not plan for the owner. It is
// reported with the step it would remove and nothing is staged against it.
const NEEDS_OWNER: StructuralStatus = 'needs the owner';

const DELETE_STEP_REASON = 'you deleted a line';
const MOVE_STEP_REASON = 'you moved a line';
const ENVELOPE_REASON = 'you changed an episode setting';
const ANSWER_REORDER_REASON = "you reordered a choice's answers";
const ANSWER_ADDED_REASON = 'you added an answer to a choice';
const ANSWER_REMOVED_REASON = 'you removed an answer';
// BAR-F14C-COPY-01: on a choice held for an unsettled identity, an answer with no line on the page
// may be one the writer deleted or one they reworded and F14c held, and the words cannot tell
// which. The row says what Basil knows rather than what the writer did.
const HELD_ANSWER_REASON = "Basil can't find this answer on the page";
const CAST_REASON = 'you changed a character or a mood';
const CHECK_REASON = 'you changed a check';

/**
 * BAR-R1-01. Both literals are now members of the shared unions in `@basil/types`
 * (`StructuralKind` and `MarkerReason`), so they are ordinary constants and no cast is left: the
 * reason keeps its own literal type, which is what `PlainMarker` asks for.
 */
export const IDENTITY_AMBIGUOUS_KIND: StructuralKind = 'identity_ambiguous';
export const AMBIGUOUS_IDENTITY_REASON = 'ambiguous identity';

/** The kind of the item an ambiguous line reports. */
function ambiguousKind(): StructuralKind {
  return IDENTITY_AMBIGUOUS_KIND;
}

/** The marker an ambiguous line raises: the candidate ids are its value. */
function ambiguousMarker(line: number, value: string): Marker {
  return { line, value, reason: AMBIGUOUS_IDENTITY_REASON };
}

/** BAR-R1-01: an ambiguity is reported by naming every id that could have been the one. */
function ambiguousIds(ids: (EngineId | null | undefined)[]): string {
  return ids.map((id) => plain(id)).join(', ');
}

function ambiguousReason(what: string): string {
  return `Basil found more than one ${what} this could be`;
}

/**
 * F14b: the second half of what the writer needs to know when a choice is held. The refusal on its
 * own says only that a line could be two answers; it does not say that a line they deleted is still
 * in the episode. When the page shows fewer lines than the choice holds answers, that is exactly
 * what happened, and the one item they already get says both things in one breath.
 */
const HOLDS_MORE_THAN_THE_PAGE_SHOWS = ', and this choice holds more answers than the page shows';

function ambiguousAnswerReason(holdsMore: boolean): string {
  return holdsMore ? `${ambiguousReason('answer')}${HOLDS_MORE_THAN_THE_PAGE_SHOWS}` : ambiguousReason('answer');
}

function structuralItem(
  kind: StructuralKind,
  details: StructuralDetails,
  status: StructuralStatus = NEEDS_CANARY,
  answerId: EngineId | null | undefined = undefined,
): StructuralItem {
  const item: StructuralItem = {
    kind, ...details, status, needsCanary: status === NEEDS_CANARY,
  };
  if (answerId !== undefined) item.answerId = answerId;
  return item;
}

// The frozen scene alignment (compile.mjs:293-343): a longest common subsequence on the heading
// signature, then, among the leftovers, one on kind alone — a renamed scene keeps its node.
function lcsPairs(before: Keyed[], after: Keyed[], key: KeyOf): [number, number][] {
  const beforeKeys = before.map(key);
  const afterKeys = after.map(key);
  const same = (i: number, j: number): boolean => beforeKeys[i] != null && beforeKeys[i] === afterKeys[j];
  const table = Array.from({ length: before.length + 1 }, () => new Uint32Array(after.length + 1));
  for (let row = before.length - 1; row >= 0; row -= 1) {
    for (let column = after.length - 1; column >= 0; column -= 1) {
      table[row][column] = same(row, column)
        ? table[row + 1][column + 1] + 1
        : Math.max(table[row + 1][column], table[row][column + 1]);
    }
  }
  const pairs: [number, number][] = [];
  let i = 0;
  let j = 0;
  while (i < before.length && j < after.length) {
    if (same(i, j)) {
      pairs.push([i, j]);
      i += 1;
      j += 1;
    } else if (table[i + 1][j] >= table[i][j + 1]) i += 1;
    else j += 1;
  }
  return pairs;
}

function alignSequence<B extends Keyed, A extends Keyed>(
  before: B[],
  after: A[],
  primary: KeyOf,
  secondary: KeyOf,
): Aligned<B, A>[] {
  const entries: Aligned<B, A>[] = [];
  const fill = (oldSlice: B[], newSlice: A[]): void => {
    let a = 0;
    let b = 0;
    lcsPairs(oldSlice, newSlice, secondary).forEach(([x, y]) => {
      while (a < x) {
        entries.push({ before: oldSlice[a], after: null });
        a += 1;
      }
      while (b < y) {
        entries.push({ before: null, after: newSlice[b] });
        b += 1;
      }
      entries.push({ before: oldSlice[x], after: newSlice[y] });
      a = x + 1;
      b = y + 1;
    });
    while (a < oldSlice.length) {
      entries.push({ before: oldSlice[a], after: null });
      a += 1;
    }
    while (b < newSlice.length) {
      entries.push({ before: null, after: newSlice[b] });
      b += 1;
    }
  };
  let i = 0;
  let j = 0;
  lcsPairs(before, after, primary).forEach(([x, y]) => {
    fill(before.slice(i, x), after.slice(j, y));
    entries.push({ before: before[x], after: after[y] });
    i = x + 1;
    j = y + 1;
  });
  fill(before.slice(i), after.slice(j));
  return entries;
}

const SIGNED: KeyOf = (item) => item.sig;
const KINDED: KeyOf = (item) => item.kind;

/** 3.1: the thirteen kinds a v3 entry may carry (the frozen `directive` is not one of them). */
const V3_KINDS = new Set<string>([
  'scene', 'route', 'character', 'narration', 'choice', 'check', 'remember',
  'ending', 'hub', 'video', 'achieve', 'roll', 'legacy',
]);

/** The v2 kind of an entry whose step is gone, carried into v3 as best it can be. */
function upgradedKind(kind: string): SidecarKind3 {
  if (kind === 'directive') return 'check';
  if (kind === 'character' || kind === 'narration' || kind === 'choice' || kind === 'hub') return kind;
  return 'legacy';
}

/**
 * 3.1: the map as version 3 entries. A v3 map is read as written; a v2 map is upgraded in memory —
 * `kind` from the named step's type, `stepTypeId` filled, `sig` ALWAYS recomputed from the step (a
 * v2 sig for a type 5/8/10 step carries the old kind token); the frozen `directive` route entry
 * becomes `route`. Any other version, a missing one included, is refused: the only staleness check
 * Round 1 adds (F-11 proper is Round 2).
 */
export function sidecarIdentities(sidecar: SidecarInput, fixture: Fixture, displayedNames: Map<string, string>): SidecarBlock3[] {
  const { version } = sidecar;
  if (version !== 2 && version !== 3) {
    throw new Error("Basil can't follow this script as it is. Open the episode again.");
  }
  return (sidecar.blocks ?? []).map((block): SidecarBlock3 => {
    const base = {
      branchId: block.branchId, stepId: block.stepId, answerIds: block.answerIds ?? [], line: block.line,
    };
    if (block.kind === 'scene') {
      // An entry that carries its signature is used as it is (the frozen code returns the same object).
      const sig = block.sig ?? sceneSignature(displayedNames.get(String(block.branchId)) ?? '');
      return {
        kind: 'scene', ...base, stepTypeId: null, sig,
      };
    }
    if (block.stepId == null) {
      return {
        kind: 'route', ...base, stepTypeId: null, sig: null,
      };
    }
    const step = findStep(findBranch(fixture, block.branchId), block.stepId);
    if (version === 3) {
      // Read as written; a kind or type the entry does not carry is read off the step it names.
      const stepTypeId = block.stepTypeId ?? step?.stepTypeId ?? null;
      const kind = V3_KINDS.has(block.kind) ? block.kind as SidecarKind3 : kindOfStepType(stepTypeId);
      const sig = block.sig ?? (step ? stepSignature(step) : null);
      return {
        kind, ...base, stepTypeId, sig,
      };
    }
    if (!step) {
      return {
        kind: upgradedKind(block.kind), ...base, stepTypeId: null, sig: block.sig ?? null,
      };
    }
    return {
      kind: kindOfStepType(step.stepTypeId), ...base, stepTypeId: step.stepTypeId, sig: stepSignature(step),
    };
  });
}

// The planner refuses an appended step that carries engine fields — it mints the
// ids itself and takes only the writer's own content — and it can only express a
// character, narration or choice step. So the request carries this reduced shape
// while the branch, the nodes output and the structural list keep the full
// engine-shaped step.
const APPENDABLE_STEP_TYPES = new Set([1, 2, 3]);

function appendRequestStep(step: Step): AppendableStep | null {
  if (!APPENDABLE_STEP_TYPES.has(step.stepTypeId)) return null;
  // The membership just checked is what makes the id appendable; the Set does not narrow the type.
  const reduced: AppendableStep = { stepTypeId: step.stepTypeId as AppendableStepTypeId };
  if (typeof step.text === 'string') reduced.text = step.text;
  if (step.stepTypeId === CHARACTER_STEP_TYPE) {
    if (step.characterId != null) reduced.characterId = step.characterId;
    if (step.characterExpressionId != null) reduced.characterExpressionId = step.characterExpressionId;
    if (step.characterPlacementSide) reduced.characterPlacementSide = step.characterPlacementSide;
  }
  if (step.stepTypeId === CHOICE_STEP_TYPE) {
    reduced.answers = (step.answers ?? []).map((answer) => ({
      // A compiled choice's answers carry the writer's own words: blockToStep copies each `*` line's text (:94).
      text: answer.text as string,
      answerPrice: Number(answer.answerPrice ?? 0),
      gotoBranchId: answer.gotoBranchId ?? null,
    }));
  }
  return reduced;
}

/**
 * D.3: apply a NAME line's cast to a step without ever assigning null for a half that did not
 * resolve — an unknown name keeps the step's character, an unknown mood keeps its expression (the
 * parser raised the marker). A mood that was not written at all is a resolved intent (no mood).
 */
function applyCast(block: CastFields, step: Step): boolean {
  const characterResolved = block.characterId != null;
  const expressionResolved = block.expression == null || block.characterExpressionId != null;
  let changed = false;
  if (characterResolved && !sameId(block.characterId, step.characterId)) {
    step.characterId = block.characterId as EngineId; // eslint-disable-line no-param-reassign
    changed = true;
  }
  if (expressionResolved && !sameId(block.characterExpressionId ?? null, step.characterExpressionId ?? null)) {
    step.characterExpressionId = block.characterExpressionId ?? null; // eslint-disable-line no-param-reassign
    changed = true;
  }
  return changed;
}

/**
 * E.5 / K.6: a remember value is the same when it is S-equal AND of the same JSON type — retyping
 * `"2"` as `2` is a change. The one leniency is the bare spelling: `> remember: name` and
 * `> forget: name` parse as the numbers 1 and 0 while the engine (ep02 throughout) stores "1" and
 * "0" as strings, and the bare line cannot spell a type, so those two numbers match their strings.
 * BAR-R1-06: that leniency belongs to the bare SPELLING, and the block now says which spelling the
 * line used. `> remember: x += 1` and `-= 1` spell their value on the page, and so does an explicit
 * `> remember: x = 1` or `= 0`: all three are typed edits and stage the bookkeeping item with the
 * number even where the engine holds the string "1". The verb cannot stand in for the spelling —
 * `parse.ts` gives the bare `> remember: x` and the explicit `> remember: x = 1` the same
 * `verb: 'set'` and the same `value: 1` — so `parse.ts` records `bare` and this function reads it.
 */
function rememberValueEqual(stepValue: unknown, blockValue: unknown, bare: boolean): boolean {
  if (!sameValue(stepValue, blockValue)) return false;
  if (stepValue == null || blockValue == null) return true;
  if (typeof stepValue === typeof blockValue) return true;
  return bare && (blockValue === 1 || blockValue === 0) && typeof stepValue === 'string';
}

/** A.3 "Moves": the clone's steps in script order, every step nobody placed kept after its old predecessor. */
function reorderSteps(steps: Step[], placed: Step[]): void {
  const placedSet = new Set(placed);
  const next: Step[] = [...placed];
  steps.forEach((step, index) => {
    if (placedSet.has(step)) return;
    let at = index - 1;
    while (at >= 0 && !next.includes(steps[at])) at -= 1;
    if (at < 0) next.unshift(step);
    else next.splice(next.indexOf(steps[at]) + 1, 0, step);
  });
  steps.splice(0, steps.length, ...next);
}

/** A.5 step 5: the clone's answers in script order, every answer nobody placed kept after its old predecessor. */
function reorderAnswers(answers: Answer[], placed: Answer[]): void {
  const placedSet = new Set(placed);
  const next: Answer[] = [...placed];
  answers.forEach((answer, index) => {
    if (placedSet.has(answer)) return;
    let at = index - 1;
    while (at >= 0 && !next.includes(answers[at])) at -= 1;
    if (at < 0) next.unshift(answer);
    else next.splice(next.indexOf(answers[at]) + 1, 0, answer);
  });
  answers.splice(0, answers.length, ...next);
}

function compileMapped(ast: ScriptAst, fixture: Fixture | undefined, sidecar: SidecarInput): MappedCompile {
  if (!fixture || !Array.isArray(fixture.branches)) {
    throw new TypeError('compile with a sidecar requires the source fixture');
  }
  const output = deepClone(fixture);
  const requests: EditRequest[] = [];
  const structural: StructuralItem[] = [];
  const compilerMarkers: Marker[] = [];
  const counters = makeCounters();
  const displayedNames = uniqueSceneNames(fixture.branches);
  const identities = sidecarIdentities(sidecar, fixture, displayedNames);
  const takenTitles = new Set<string>(output.branches.map((branch) => String(branch.title ?? '')));

  // Scenes are matched on their heading first and, for whatever the headings do
  // not settle, on their position among the leftovers — a renamed scene keeps
  // its node instead of reading as one node deleted and another invented.
  const sceneIdentities = identities.filter((block) => block.kind === 'scene');
  const scenePairs = alignSequence<IdentityItem, SceneItem>(
    sceneIdentities.map((identity) => ({ identity, sig: identity.sig, kind: 'scene' })),
    ast.scenes.map((scene) => ({ scene, sig: sceneSignature(scene.name), kind: 'scene' })),
    SIGNED,
    KINDED,
  );
  const sceneBranches = new Map<Scene, Branch | undefined>();
  const droppedScenes: SidecarBlock3[] = [];
  scenePairs.forEach((pair) => {
    if (!pair.after) {
      if (pair.before) droppedScenes.push(pair.before.identity);
    } else if (pair.before) {
      sceneBranches.set(pair.after.scene, findBranch(output, pair.before.identity.branchId));
    }
  });

  const stepIdentities = new Map<string, SidecarBlock3[]>();
  identities.forEach((block) => {
    if (block.kind === 'scene' || block.stepId == null) return;
    const key = String(block.branchId);
    const known = stepIdentities.get(key);
    if (known) known.push(block);
    else stepIdentities.set(key, [block]);
  });

  const newSceneIds = new Map<Scene, number>();
  ast.scenes.forEach((scene) => {
    if (!sceneBranches.get(scene)) newSceneIds.set(scene, counters.branch());
  });
  const targets: Targets = new Map(
    [...displayedNames.entries()].map(([branchId, name]): [string, EngineId | undefined] => [
      name.toLocaleLowerCase(), findBranch(output, branchId)?.id,
    ]),
  );
  ast.scenes.forEach((scene) => {
    const branch = sceneBranches.get(scene);
    targets.set(scene.name.toLocaleLowerCase(), branch?.id ?? newSceneIds.get(scene));
  });

  const addStructural = (
    kind: StructuralKind,
    scene: Scene | null,
    identity: IdentityHint | null,
    reason: string,
    status: StructuralStatus = NEEDS_CANARY,
    answerId: EngineId | null | undefined = undefined,
  ): void => {
    structural.push(structuralItem(kind, {
      scene: scene?.name ?? displayedNames.get(String(identity?.branchId)) ?? null,
      branchId: identity?.branchId ?? (scene ? sceneBranches.get(scene) : undefined)?.id ?? null,
      stepId: identity?.stepId ?? null,
      line: identity?.line ?? scene?.line ?? null,
      reason,
    }, status, answerId));
  };

  ast.scenes.forEach((scene) => {
    const branch = sceneBranches.get(scene);
    if (!branch) {
      // Every scene without a node was given an id above (compile.mjs:439-442). F.2 names it.
      const title = uniqueSlug(slugTitle(scene.name), takenTitles);
      takenTitles.add(title);
      const created = makeBranch(
        scene,
        newSceneIds.get(scene) as number,
        targets,
        counters,
        null,
        { title, description: scene.name },
        compilerMarkers,
      );
      output.branches.push(created);
      addStructural('new_scene', scene, null, 'you added a scene');
      return;
    }
    const originalDisplay = displayedNames.get(String(branch.id));
    if (scene.name !== originalDisplay || (scene.locationId != null && !sameId(scene.locationId, branch.locationId))) {
      if (scene.name !== originalDisplay) {
        // A.6 / F.2: a renamed scene keeps its node; the heading becomes the description and the
        // title its slug, unique among the other nodes.
        const others = new Set(takenTitles);
        others.delete(String(branch.title ?? ''));
        branch.title = uniqueSlug(slugTitle(scene.name), others);
        branch.description = scene.name;
        takenTitles.add(branch.title);
      }
      if (scene.locationId != null) branch.locationId = scene.locationId;
      addStructural(
        'scene_metadata',
        scene,
        { branchId: branch.id, stepId: null, line: scene.line },
        "you changed a scene's title or location",
      );
    }

    scene.blocks.forEach((block) => {
      if (block.type !== 'directive' || block.directive !== 'goto') return;
      const desired = targetId(block, targets);
      if (!sameId(desired, branch.gotoBranchId)) {
        branch.gotoBranchId = desired;
        addStructural(
          'branch_route',
          scene,
          { branchId: branch.id, stepId: null, line: block.line },
          'you changed where this scene goes next',
        );
      }
    });

    // The sidecar was written from this node's steps[], so a mapped node carries one: this is the
    // frozen `branch.steps`, read the same way.
    if (!branch.steps) branch.steps = [];
    const { steps } = branch;
    const entries = stepIdentities.get(String(branch.id)) ?? [];
    const stepBlocks = scene.blocks.filter(isStepBlock);
    const alignment = alignSteps(
      entries.map((entry): AlignableEntry => {
        const step = findStep(branch, entry.stepId);
        return {
          entry,
          kind: entry.kind,
          sig: entry.sig,
          // BAR-R1-01: the exact words the entry's signature normalised, so an out-of-order match
          // between two lines that differ only in case or spacing is a move, not two text edits.
          raw: step ? rawKey(entry.sig, stepRawText(step)) : null,
          text: step ? stepPairingText(step) : '',
        };
      }),
      stepBlocks.map((block): AlignableBlock<Block> => {
        const sig = round1BlockSignature(block);
        return {
          block,
          kind: blockKind(block) as SidecarKind3,
          sig,
          raw: rawKey(sig, blockRawText(block)),
          text: blockPairingText(block),
        };
      }),
    );
    const pairByBlock = new Map(alignment.pairs.map((pair) => [pair.blockIndex, pair]));
    const ambiguousByBlock = new Map(alignment.ambiguous.map((item) => [item.blockIndex, item]));
    const pairedEntryIndices = alignment.pairs.map((pair) => pair.entryIndex).sort((a, b) => a - b);
    // A deletion is reported where the frozen merged order put it: after the nearest paired entry
    // before it in the map, before whatever the script inserts there.
    const deletionsAfter = new Map<number, number[]>();
    alignment.deletedEntries.forEach((entryIndex) => {
      let anchor = -1;
      pairedEntryIndices.forEach((paired) => {
        if (paired < entryIndex) anchor = paired;
      });
      const list = deletionsAfter.get(anchor);
      if (list) list.push(entryIndex);
      else deletionsAfter.set(anchor, [entryIndex]);
    });
    const lastPairedBlock = alignment.pairs.reduce((last, pair) => Math.max(last, pair.blockIndex), -1);
    let insertAt = 0;
    const placed: Step[] = [];
    let anyMove = false;
    const processDeletions = (anchor: number): void => {
      (deletionsAfter.get(anchor) ?? []).forEach((entryIndex) => {
        const identity = entries[entryIndex];
        const step = findStep(branch, identity.stepId);
        if (step) insertAt = steps.indexOf(step) + 1;
        // A deletion is reported against the step it would remove and nothing
        // is staged: the fixture keeps the step until the owner says otherwise.
        addStructural('delete_step', scene, identity, DELETE_STEP_REASON, NEEDS_OWNER);
      });
    };
    processDeletions(-1);

    // setStepText, compile.mjs:458-462: a changed text is applied and staged as one request.
    const setStepText = (step: Step, text: string): void => {
      if (blankEqual(step.text, text)) return;
      step.text = text; // eslint-disable-line no-param-reassign
      requests.push({
        op: 'set_step_text', branchId: branch.id, stepId: step.id, text,
      });
    };

    const applyChoice = (block: ChoiceBlock, step: Step, identity: SidecarBlock3): void => {
      setStepText(step, block.text);
      // D.3: the NAME line above the prompt is the choice's speaker; no NAME line leaves the cast alone.
      if (block.name != null && applyCast(block, step)) addStructural('character_cast', scene, identity, CAST_REASON);
      const answers = step.answers ?? [];
      const known = identity.answerIds
        .map((id) => answers.find((candidate) => sameId(candidate.id, id)))
        .filter((answer): answer is Answer => answer !== undefined);
      // G.2: an answer the parser dropped as empty pairs, in order, with an existing blank answer
      // when the counts agree; those take no part in the alignment.
      let existing = known;
      const dropped = block.droppedAnswers ?? 0;
      if (dropped > 0 && block.answers.length + dropped === identity.answerIds.length) {
        let budget = dropped;
        existing = known.filter((answer) => {
          if (budget > 0 && normaliseText(answer.text) === '') {
            budget -= 1;
            return false;
          }
          return true;
        });
      }
      // BAR-R1-01b: the identity of an answer is the whole line the writer sees — the words, the
      // price the bracket spells and the branch the arrow resolves to — so two answers whose words
      // are the same are still told apart, and a swap of them moves the objects (rewards, routes,
      // alternate prices and all) instead of repricing the ids that stayed put.
      const answerAlignment = alignAnswers(
        existing.map((answer): AnswerIdentity => ({
          id: answer.id as EngineId,
          text: answer.text,
          price: Number(answer.answerPrice ?? 0),
          destination: answer.gotoBranchId ?? null,
        })),
        block.answers,
        block.answers.map((answer) => targetId(answer, targets)),
      );
      if (answerAlignment.reordered) addStructural('choice_shape', scene, identity, ANSWER_REORDER_REASON, NEEDS_CANARY, null);
      const pairByParsed = new Map(answerAlignment.pairs.map((pair) => [pair.parsedIndex, pair]));
      const ambiguousByParsed = new Map(answerAlignment.ambiguous.map((item) => [item.parsedIndex, item]));
      // F14, with the owner's disposition of 2026-09-07: while any answer identity on this choice is
      // ambiguous, no unmatched free line is staged. A free line is either the twin the writer just
      // edited or an answer typed in the same sitting, and the words alone cannot tell those apart,
      // so the compiler keeps the same silence the step lane keeps around a line it could not name.
      // The precise guarantee: unmatched lines are not staged; refused candidate answers remain
      // intact; matched answers keep their existing edit and reorder behaviour. The refusal, its
      // marker and its owner status are untouched, and a choice with no ambiguity on it takes a new
      // answer as it always did. Holding is conservative: the desired answers need not match the
      // edited page line for line until the ambiguity is resolved.
      const heldByAmbiguity = answerAlignment.ambiguous.length > 0;
      // F14b: the alignment no longer lets position claim a line on a choice it has refused, so the
      // lines a refusal leaves over arrive here as additions and the answers nobody claimed as
      // removals — held, both of them, instead of one line's words landing on the other's id with
      // its price and its route stripped. When the page is short of the answers the choice holds,
      // the refusal says so, so the writer is not left thinking a line they deleted is gone.
      const holdsMoreThanThePageShows = heldByAmbiguity && block.answers.length < existing.length;
      let freshAnswers = 0;
      const placedAnswers: Answer[] = [];
      block.answers.forEach((answer, index) => {
        const pair = pairByParsed.get(index);
        const uncertain = ambiguousByParsed.get(index);
        if (uncertain) {
          // BAR-R1-01, the answer side: no text is written onto a candidate's id and no answer is
          // added or removed; every candidate keeps its price, route, rewards and id.
          const ids = ambiguousIds(uncertain.existingIndices.map((at) => existing[at].id));
          compilerMarkers.push(ambiguousMarker(answer.line, ids));
          addStructural(
            ambiguousKind(),
            scene,
            { branchId: branch.id, stepId: step.id, line: answer.line },
            ambiguousAnswerReason(holdsMoreThanThePageShows),
            NEEDS_OWNER,
            null,
          );
          return;
        }
        if (!pair) {
          // Held with the refusal above: no answer is made up, and the choice keeps the answers it has.
          if (heldByAmbiguity) return;
          const fresh = freshAnswer(answer, counters, targets);
          placedAnswers.push(fresh);
          freshAnswers += 1;
          addStructural('choice_shape', scene, identity, ANSWER_ADDED_REASON, NEEDS_CANARY, fresh.id);
          return;
        }
        const existingAnswer = existing[pair.existingIndex];
        placedAnswers.push(existingAnswer);
        if (!blankEqual(existingAnswer.text, answer.text)) {
          existingAnswer.text = answer.text;
          requests.push({
            // Bound by its own id through the alignment, so this is the id the map recorded.
            op: 'set_answer_text', branchId: branch.id, stepId: step.id, answerId: existingAnswer.id as EngineId, text: answer.text,
          });
        }
        const desiredTarget = targetId(answer, targets);
        const priceChanged = Number(existingAnswer.answerPrice ?? 0) !== Number(answer.price ?? 0);
        const routeChanged = !sameId(existingAnswer.gotoBranchId ?? null, desiredTarget);
        if (priceChanged || routeChanged) {
          existingAnswer.answerPrice = answer.price ?? 0;
          existingAnswer.gotoBranchId = desiredTarget;
          // F.5: the answer type follows the price.
          if (priceChanged) existingAnswer.answerTypeId = answerTypeForPrice(answer.price ?? 0, existingAnswer.answerTypeId);
          addStructural('choice_route_or_price', scene, identity, 'you changed a price or where an answer goes');
        }
      });
      answerAlignment.removed.forEach((existingIndex) => {
        // NOTHING is staged and the clone keeps the answer: a removal needs the owner. On a held
        // choice the answer may be a deletion or a reworded line F14c is holding, so the row says
        // only that the page has no line for it (BAR-F14C-COPY-01).
        addStructural(
          'choice_shape',
          scene,
          identity,
          heldByAmbiguity ? HELD_ANSWER_REASON : ANSWER_REMOVED_REASON,
          NEEDS_OWNER,
          existing[existingIndex].id ?? null,
        );
      });
      // `freshAnswers`, not the alignment's own count: a held free line made no object to merge.
      // A held choice can still take the pre-existing reorder path for its matched answers.
      if (answerAlignment.reordered || freshAnswers > 0) {
        // The SAME answer objects travel, so autoAchievements, requirement, alternatePrices,
        // modifiers, traits, requires, data, tags, elimination fields and uuid move with them.
        step.answers = answers; // eslint-disable-line no-param-reassign
        reorderAnswers(answers, placedAnswers);
      }
    };

    const applyCheck = (block: IfDirective, step: Step, identity: SidecarBlock3): void => {
      // E.2: an unknown operator changes nothing — no request, no item, the step's objects kept.
      if (block.operator === null) return;
      const check = step.check ?? {};
      const arms = step.switch ?? [];
      const variableId = block.variableId != null ? block.variableId : (check.variableId ?? null);
      const desiredArms = block.arms.map((arm): SwitchArm => ({ value: arm.value, gotoBranchId: targetId(arm, targets) }));
      // E.6: field by field with S-equality; when nothing differs the clone keeps its own objects.
      const checkChanged = !sameValue(check.variableId ?? null, variableId)
        || !sameValue(check.operator, block.operator)
        || !sameValue(check.value, block.value);
      const armsChanged = arms.length !== desiredArms.length || desiredArms.some((arm, index) => (
        !sameValue(arms[index].value, arm.value) || !sameId(arms[index].gotoBranchId ?? null, arm.gotoBranchId)));
      if (!checkChanged && !armsChanged) return;
      if (checkChanged) {
        step.check = { // eslint-disable-line no-param-reassign
          ...check, variableId, operator: block.operator, value: block.value,
        };
      }
      if (armsChanged) {
        step.switch = desiredArms.map((arm, index) => ( // eslint-disable-line no-param-reassign
          arms[index] ? { ...arms[index], value: arm.value, gotoBranchId: arm.gotoBranchId } : arm));
      }
      addStructural('check', scene, identity, CHECK_REASON);
    };

    const applyEnvelope = (block: CheckEnvelope, step: Step, identity: SidecarBlock3): void => {
      // E.4 / BAR-R1-05: JSON-equal, types and all → nothing; different → the clone's check and
      // switch are the parsed objects. The two spellings compared are the two the decompiler wrote
      // (`check ?? {}`, `switch ?? []`), so a script nobody touched is equal to itself.
      if (jsonEqual(step.check ?? {}, block.payload.check) && jsonEqual(step.switch ?? [], block.payload.switch)) return;
      step.check = block.payload.check as Step['check']; // eslint-disable-line no-param-reassign
      step.switch = block.payload.switch as SwitchArm[]; // eslint-disable-line no-param-reassign
      addStructural('check', scene, identity, CHECK_REASON);
    };

    const applyRemember = (block: SetDirective, step: Step, identity: SidecarBlock3): void => {
      const action = step.action ?? {};
      const verb = block.verb ?? null;
      const variableId = block.variableId != null ? block.variableId : (action.variableId ?? null);
      // E.5: a line that spells no verb (the frozen parser) leaves the step's type as it is.
      const typeChanged = verb !== null && plain(action.type) !== verb;
      const variableChanged = !sameId(action.variableId ?? null, variableId);
      const valueChanged = !rememberValueEqual(action.value, block.value, block.bare === true);
      if (!typeChanged && !variableChanged && !valueChanged) return;
      step.action = { // eslint-disable-line no-param-reassign
        ...action, type: verb ?? (plain(action.type) || 'set'), variableId, value: block.value,
      };
      addStructural('bookkeeping', scene, identity, 'you changed a memory line');
    };

    const applyHub = (block: HubBlock, step: Step, identity: SidecarBlock3): void => {
      const ordered = [...(step.hubOptions ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      const desiredOptions = block.options.map((option, index) => ({
        title: option.text, gotoBranchId: targetId(option, targets), price: option.price ?? 0, index,
      }));
      const settingsChanged = !blankEqual(step.hubSettings?.header?.text, block.header)
        || !blankEqual(step.hubSettings?.subheader?.text, block.subheader);
      const optionsChanged = ordered.length !== desiredOptions.length || ordered.some((option, index) => {
        const desired = desiredOptions[index];
        return !blankEqual(option.title, desired?.title) || !sameId(option.gotoBranchId ?? null, desired?.gotoBranchId)
          || Number(option.answerPrice ?? option.price ?? 0) !== Number(desired?.price ?? 0);
      });
      if (!settingsChanged && !optionsChanged) return;
      // compile.mjs:597-599 written out: `??=` keeps what is there and fills what is null or absent.
      const settings: HubSettings = step.hubSettings ?? { header: {}, subheader: {}, progressBars: [] };
      step.hubSettings = settings; // eslint-disable-line no-param-reassign
      if (settings.header == null) settings.header = {};
      if (settings.subheader == null) settings.subheader = {};
      settings.header.text = block.header;
      settings.subheader.text = block.subheader;
      block.options.forEach((option, index) => {
        const target = ordered[index];
        if (!target) return;
        target.title = option.text;
        target.gotoBranchId = targetId(option, targets);
        if ('answerPrice' in target || Number(option.price) > 0) target.answerPrice = option.price ?? 0;
      });
      addStructural('hub', scene, identity, 'you changed a hub');
    };

    const applyVideo = (block: VideoBlock, step: Step): void => {
      // C, type 14: the label is the library's; a changed one is a marker, nothing is staged.
      if (block.label.trim() !== videoLabel(step)) {
        compilerMarkers.push({ line: block.line, value: block.label, reason: 'read-only label' });
      }
      setStepText(step, block.text);
    };

    const applyLegacy = (block: LegacyBlock, step: Step, identity: SidecarBlock3): void => {
      const typeChanged = Number(step.stepTypeId) !== block.stepTypeId;
      const subTypeChanged = block.stepTypeId === 6 && !blankEqual(step.subType, block.subType);
      const textChanged = !blankEqual(step.text, block.text);
      if (!typeChanged && !subTypeChanged && !textChanged) return;
      step.stepTypeId = block.stepTypeId; // eslint-disable-line no-param-reassign
      if (block.stepTypeId === 6) step.subType = block.subType; // eslint-disable-line no-param-reassign
      step.text = block.text; // eslint-disable-line no-param-reassign
      addStructural('envelope', scene, identity, ENVELOPE_REASON);
    };

    const applyBlock = (block: Block, step: Step, identity: SidecarBlock3): void => {
      if (block.type === 'character') {
        setStepText(step, block.text);
        if (applyCast(block, step)) addStructural('character_cast', scene, identity, CAST_REASON);
        const wanted = characterStepType(block);
        if ([1, 8, 9, 10].includes(Number(step.stepTypeId)) && Number(step.stepTypeId) !== wanted) {
          step.stepTypeId = wanted; // eslint-disable-line no-param-reassign
          addStructural('envelope', scene, identity, ENVELOPE_REASON);
        }
      } else if (block.type === 'narration') {
        setStepText(step, block.text);
      } else if (block.type === 'choice') {
        applyChoice(block, step, identity);
      } else if (block.type === 'directive' && block.directive === 'if') {
        applyCheck(block, step, identity);
      } else if (block.type === 'directive' && block.directive === 'check') {
        applyEnvelope(block, step, identity);
      } else if (isRememberBlock(block)) {
        applyRemember(block, step, identity);
      } else if (block.type === 'directive' && block.directive === 'end') {
        setStepText(step, endText(block, scene.name));
      } else if (block.type === 'hub') {
        applyHub(block, step, identity);
      } else if (block.type === 'video') {
        applyVideo(block, step);
      } else if (block.type === 'achieve') {
        if (!blankEqual(step.achievementDescription, block.description)) {
          step.achievementDescription = block.description; // eslint-disable-line no-param-reassign
          addStructural('envelope', scene, identity, ENVELOPE_REASON);
        }
      } else if (block.type === 'roll') {
        const desired = rollFromBlock(block, block.variableId != null ? block.variableId : (step.roll?.variableId ?? null));
        if (!sameValue(step.roll, desired)) {
          step.roll = desired; // eslint-disable-line no-param-reassign
          addStructural('envelope', scene, identity, ENVELOPE_REASON);
        }
      } else if (block.type === 'legacy') {
        applyLegacy(block, step, identity);
      }
    };

    stepBlocks.forEach((block, blockIndex) => {
      const pair = pairByBlock.get(blockIndex);
      if (!pair) {
        const uncertain = ambiguousByBlock.get(blockIndex);
        if (uncertain) {
          // BAR-R1-01: two or more steps could be this line and the words do not say which. Nothing
          // is staged, nothing is created and nothing is deleted: the clone keeps every candidate
          // exactly as the engine holds it, and the owner is asked on the line itself.
          const ids = ambiguousIds(uncertain.entryIndices.map((index) => entries[index].stepId));
          compilerMarkers.push(ambiguousMarker(block.line, ids));
          addStructural(
            ambiguousKind(),
            scene,
            { branchId: branch.id, stepId: null, line: block.line },
            ambiguousReason('line'),
            NEEDS_OWNER,
          );
          return;
        }
        const fresh = blockToStep(block, branch.id, targets, counters, null, compilerMarkers, scene.name);
        if (!fresh) return;
        placed.push(fresh);
        // Everything past the last block the map knew about is an append; anything
        // before it lands between steps the engine already has, which is the one
        // shape the per-step lane cannot express.
        if (blockIndex > lastPairedBlock) {
          steps.push(fresh);
          const appended = appendRequestStep(fresh);
          if (appended) requests.push({ op: 'append_step', branchId: branch.id, step: appended });
          addStructural(
            'append_step',
            scene,
            { branchId: branch.id, stepId: fresh.id, line: block.line },
            'you added a line at the end of a scene',
          );
          return;
        }
        steps.splice(insertAt, 0, fresh);
        insertAt += 1;
        addStructural(
          'insert_step',
          scene,
          { branchId: branch.id, stepId: fresh.id, line: block.line },
          'you added a line in the middle of a scene',
        );
        return;
      }
      const identity = entries[pair.entryIndex];
      const step = findStep(branch, identity.stepId);
      if (!step) {
        addStructural('missing_identity', scene, identity, "this line isn't in the episode any more");
        processDeletions(pair.entryIndex);
        return;
      }
      insertAt = steps.indexOf(step) + 1;
      placed.push(step);
      applyBlock(block, step, identity);
      if (pair.moved) {
        // A.4: a moved line keeps its id and its attachments; the clone takes the script's order.
        anyMove = true;
        addStructural('move_step', scene, { branchId: branch.id, stepId: step.id, line: block.line }, MOVE_STEP_REASON);
      }
      processDeletions(pair.entryIndex);
    });
    if (anyMove) reorderSteps(steps, placed);
  });

  droppedScenes.forEach((identity) => {
    addStructural('delete_scene', null, identity, 'you deleted a scene', NEEDS_OWNER);
  });

  return {
    mode: 'mapped',
    nodes: output.branches,
    fixture: output,
    requests,
    structural,
    markers: compilerMarkers.length ? [...ast.markers, ...compilerMarkers] : ast.markers,
  };
}

export function compile(ast: ScriptAst, options: CompileOptions = {}): CompileResult {
  if (!ast || !Array.isArray(ast.scenes)) throw new TypeError('compile requires a parsed script AST');
  return options.sidecar ? compileMapped(ast, options.fixture, options.sidecar) : compileFresh(ast);
}

export { graphReport as structureReport };
